import { Injectable } from '@angular/core';
import { NewsfeedApiService } from '@epicuro-next/data/engagement/engagement-api';
import { mapLegacyMediaRequestToEngagement } from '@epicuro-next/data/engagement/engagement-api-utils';
import {
  NewsfeedPromotesDto,
  NewsfeedType,
  NewsfeedUpdateRequestDto,
} from '@epicuro-next/data/engagement/engagement-models';
import { MessageType, showSnackBarMessage } from '@epicuro-next/state/snackbar';
import { isCurrentDay, StrictOmit } from '@epicuro-next/utilities';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, filter, map, switchMap } from 'rxjs/operators';

import * as actions from '../actions';

export const SUCCESS_MESSAGE = $localize`:@@NewsFeedUpdateSuccess:Item updated.`;
export const FAILED_MESSAGE = $localize`:@@NewsFeedUpdateFailed:Failed to update.`;

/** News feed edit effects */
@Injectable()
export class EditEffects {
  /** Edit article action effect */
  public editArticle$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.editArticle),
      switchMap((action) => {
        const payload: NewsfeedUpdateRequestDto = {
          ...action.article,
          media: action.article.media
            ? mapLegacyMediaRequestToEngagement(action.article.media)
            : undefined,
        };
        return this.feedService.update(action.article.id, payload).pipe(
          map(() => this.setUpdateActionsBasedOnConditions(action)),
          catchError(() => of(actions.editArticleFail())),
        );
      }),
    ),
  );

  public updatePromoteByReferences$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.editArticle),
      filter(({ article }) => Boolean(article.promoteReferences)),
      switchMap(({ article }) => {
        return this.feedService
          .updatePromoteByReferences({
            id: article.id,
            promoteReference: article.promoteReferences as NewsfeedPromotesDto,
          })
          .pipe(
            map(() =>
              actions.editArticlePromoteReferencesSuccess({
                promoteReferences:
                  article.promoteReferences as NewsfeedPromotesDto,
                id: article.id,
              }),
            ),
            catchError(() => of(actions.editArticlePromoteReferencesFail())),
          );
      }),
    ),
  );

  public showSuccessMessage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        actions.editArticleSuccess,
        actions.editArticleRemoveFromTodaySpotlight,
        actions.editArticleWithPromotionDateUpdateSuccess,
      ),
      map(() =>
        showSnackBarMessage({
          message: SUCCESS_MESSAGE,
          messageType: MessageType.SUCCESS,
        }),
      ),
    ),
  );

  public showFailedMessage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.editArticleFail),
      map(() =>
        showSnackBarMessage({
          message: FAILED_MESSAGE,
          messageType: MessageType.DANGER,
        }),
      ),
    ),
  );

  /** Dependencies */
  constructor(
    private actions$: Actions,
    private feedService: NewsfeedApiService,
  ) {}

  private checkIfDateIsToday(date?: string) {
    if (!date) return;
    return isCurrentDay(new Date(date));
  }

  private setUpdateActionsBasedOnConditions({
    article,
    previousPromotionStartDate,
  }: StrictOmit<ReturnType<typeof actions.editArticle>, 'type'>) {
    if (article.newsfeedType === NewsfeedType.WelcomePromotion)
      return actions.editArticleSuccess({ article });

    const promotionStartDate = article?.range ? article.range[0] : undefined;
    const isPromotionStartToday = this.checkIfDateIsToday(promotionStartDate);
    const isPublishedArticleVisibleToSpotlight = this.checkIfDateIsToday(
      previousPromotionStartDate,
    );

    if (isPromotionStartToday) {
      return isPublishedArticleVisibleToSpotlight
        ? actions.editArticleSuccess({ article })
        : actions.editArticleWithPromotionDateUpdateSuccess();
    }

    return actions.editArticleRemoveFromTodaySpotlight({
      feedId: article.id,
    });
  }
}
